import { brandAssets } from '@echo/brand';
import mascotOffline from '@echo/brand/assets/mascot-offline.png';
import { useUpdateRequired } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EmptyStateArt, EmptyStateFrame } from './empty-state-frame';

/**
 * Core refused this build as older than it supports (`UNSUPPORTED_APP_VERSION`), as the OE app
 * answers with "Refresh Page". It covers everything, the offline screen included, and takes
 * the focus. Everything else on the page goes inert, sheets open in their own portals included,
 * so nothing behind it can be reached. A reload fetches the current build. The web keeps its
 * outbox for the visit only, so a save Core refused is lost with the reload
 * (specs/v1-0-0/08-cross-cutting.md › Offline).
 */
export function UpdateGate({ reload = () => window.location.reload() }: { reload?: () => void }) {
  const { t } = useLingui();
  const required = useUpdateRequired();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const own = dialog.current;
    if (!required || !own) return;
    const others = Array.from(document.body.children).filter(
      (element) => !element.contains(own) && !element.hasAttribute('inert'),
    );
    for (const element of others) element.setAttribute('inert', '');
    own.focus();
    return () => {
      for (const element of others) element.removeAttribute('inert');
    };
  }, [required]);
  if (!required) return null;
  return createPortal(
    <div
      ref={dialog}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-surface-secondary px-gutter py-section-md"
    >
      <div className="w-full max-w-[800px]">
        <EmptyStateFrame
          art={
            <EmptyStateArt
              src={mascotOffline}
              alt=""
              width={brandAssets.mascotOffline.width}
              height={brandAssets.mascotOffline.height}
            />
          }
          title={
            <span id="update-title">
              <Trans>Update StrongerMemory</Trans>
            </span>
          }
          actions={<Button onClick={reload}>{t`Reload page`}</Button>}
        >
          <Trans>
            This version of StrongerMemory is out of date. Reload the page to get the latest one.
          </Trans>
        </EmptyStateFrame>
      </div>
    </div>,
    document.body,
  );
}
